import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { isFocusable } from "../../../models/utilities/defaultFocusStatusUtilities";
import { KeyboardNavigation } from "./KeyboardNavigation";

export class ChoiceGridRendererNavigation extends KeyboardNavigation {
    // variableIds will be computed dynamically from defaultFocusStatus.statuses
    // to reflect the actual rendered order (supports Permutation/randomization).

    getVariableIdsForQuestion(questionId: number) {
        return this.defaultFocusStatus.statuses
            .filter((s) => s.questionId === questionId)
            .map((s) => s.variableId)
            .filter((v, i, arr) => arr.indexOf(v) === i);
    }

    private findFocusableChoiceWithinCurrentVariable(
        currentElement: DefaultFocusStatusModel,
        step: 1 | -1,
    ): DefaultFocusStatusModel | null {
        return this.findFocusableChoiceWithinVariable(
            currentElement,
            currentElement.variableId,
            step,
            true,
        );
    }

    private findFocusableChoiceWithinVariable(
        currentElement: DefaultFocusStatusModel,
        variableId: number,
        step: 1 | -1,
        excludeCurrentElement = false,
    ): DefaultFocusStatusModel | null {
        const startIndex = this.defaultFocusStatus.statuses.indexOf(currentElement) + step;
        const isInsideBounds = (i: number) =>
            i >= 0 && i < this.defaultFocusStatus.statuses.length;

        for (let i = startIndex; isInsideBounds(i); i += step) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            if (
                elementFocusStatus.questionId === currentElement.questionId &&
                elementFocusStatus.variableId === variableId &&
                (!excludeCurrentElement ||
                    elementFocusStatus.elementId !== currentElement.elementId) &&
                elementFocusStatus.elementRef?.current &&
                isFocusable(elementFocusStatus)
            ) {
                return elementFocusStatus;
            }
        }

        return null;
    }

    findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        if (this.isHybridGrid && (eventKey === "ArrowLeft" || eventKey === "ArrowRight")) {
            return this.findNextFocusableElementWithinSameVariableCommonForHybridGrid(
                currentElement,
                eventKey,
            );
        }

        if (this.isHybridGrid && (eventKey === "ArrowDown" || eventKey === "ArrowUp")) {
            return this.findNextFocusableElementWithinSameVariableCommonForSwitchingRowsForHybridGrid(
                currentElement,
                eventKey,
            );
        }

        // Compute variableIds in DOM/render order for this question so navigation follows
        // any runtime permutation/shuffle applied to columns/rows.
        const variableIds = this.getVariableIdsForQuestion(currentElement.questionId);

        let nextVariableId = currentElement.variableId;
        const currentVariableIndex = variableIds.indexOf(currentElement.variableId);

        // Helper to check if an element is visible
        // Helper to check if a specific element DOM node is visible.
        // If elementRef.current is missing, try finding it by id.
        const isDomNodeVisible = (el) => {
            if (!el) return false;
            const node = el.current ? el.current : document.getElementById(el.id || el.elementId);
            if (!node) return false;
            const style = window.getComputedStyle(node);
            return style.display !== "none" && style.visibility !== "hidden";
        };

        /* Row open-ends share their row's variableId but are not grid columns — they sit
         * before the row's cells in the focus store and arrows on the textarea itself are
         * caret-only. Excluding them keeps the column math aligned across rows with and
         * without open-ends, and stops ArrowUp/Down from landing on a textarea. */
        const isRowCell = (s: DefaultFocusStatusModel) => !s.elementId.endsWith("_OE_INPUT");

        /* Column position is the respondent's *visual* column — computed over every *visible*
         * cell in the row, focusable or not. The focus store is append-only (unmount does not
         * splice entries), so a shouldHideCell / unmounted cell can leave a stale status with a
         * dead ref; next/prev row lists already drop those via isDomNodeVisible, and the current
         * row must use the same filter or columnIndexInRow is inflated and ArrowUp/Down shift.
         * Do not pre-filter by isFocusable here — that would base the count on a different set
         * than the target row (a disabled cell in either row silently shifts alignment).
         * Focusability is checked only when picking a candidate, via pickFocusableNearColumn. */
        const isStatusDomVisible = (s: DefaultFocusStatusModel) =>
            isDomNodeVisible(
                s.elementRef
                    ? s.elementRef
                    : {
                          id: s.elementId,
                          elementId: s.elementId,
                      },
            );
        const elementsInCurrentRow = this.defaultFocusStatus.statuses.filter(
            (s, i) =>
                s.variableId === currentElement.variableId &&
                i <= index &&
                isRowCell(s) &&
                isStatusDomVisible(s),
        );
        const columnIndexInRow = elementsInCurrentRow.length - 1;

        /* Finds the cell nearest the target column that can actually receive focus, expanding
         * outward (forward, then backward) so a disabled/eliminated cell doesn't break column
         * alignment for the rest of the row, and a run of disabled cells doesn't strand focus. */
        const pickFocusableNearColumn = (
            rowCells: { status: DefaultFocusStatusModel; index: number }[],
            targetColumn: number,
        ) => {
            if (!rowCells.length) return undefined;
            const clamped = Math.max(0, Math.min(targetColumn, rowCells.length - 1));
            for (let offset = 0; offset < rowCells.length; offset++) {
                const forward = clamped + offset;
                if (forward < rowCells.length && isFocusable(rowCells[forward].status)) {
                    return rowCells[forward];
                }
                const backward = clamped - offset;
                if (offset > 0 && backward >= 0 && isFocusable(rowCells[backward].status)) {
                    return rowCells[backward];
                }
            }
            return undefined;
        };

        if (eventKey === "ArrowDown") {
            for (let nextIdx = currentVariableIndex + 1; nextIdx < variableIds.length; nextIdx++) {
                const nextVarId = variableIds[nextIdx];
                const elementsInNextRow = this.defaultFocusStatus.statuses
                    .map((s, i) => ({ status: s, index: i }))
                    .filter(
                        (x) =>
                            x.status.variableId === nextVarId &&
                            x.index > index &&
                            isRowCell(x.status) &&
                            isStatusDomVisible(x.status),
                    );
                const targetElement = pickFocusableNearColumn(elementsInNextRow, columnIndexInRow);
                if (targetElement && targetElement.status.elementId !== currentElement.elementId) {
                    return targetElement.status;
                }
            }
            return null;
        }
        // Find previous visible row for ArrowUp
        if (eventKey === "ArrowUp") {
            for (let prevIdx = currentVariableIndex - 1; prevIdx >= 0; prevIdx--) {
                const prevVarId = variableIds[prevIdx];
                const elementsInPrevRow = this.defaultFocusStatus.statuses
                    .map((s, i) => ({ status: s, index: i }))
                    .filter(
                        (x) =>
                            x.status.variableId === prevVarId &&
                            x.index < index &&
                            isRowCell(x.status) &&
                            isStatusDomVisible(x.status),
                    );
                const targetElement = pickFocusableNearColumn(elementsInPrevRow, columnIndexInRow);
                if (targetElement && targetElement.status.elementId !== currentElement.elementId) {
                    return targetElement.status;
                }
            }
            return null;
        }
        /* Arrows move between cells only — an open-end textarea has no arrow way out (it
         * forwards Tab/Shift+Tab only; arrows are caret-only once focus is inside it, see
         * OpenEndRenderer), so arrows must not be able to enter one either, or a respondent
         * who arrows in gets stuck without a way back except Tab. Mirrors ButtonChoiceNavigation's
         * same choice for radio/checkbox lists. */
        if (eventKey === "ArrowRight") {
            for (let i = 0; i < this.defaultFocusStatus.statuses.length; i++) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    i > index &&
                    elementFocusStatus.variableId === nextVariableId &&
                    elementFocusStatus.elementId !== currentElement.elementId &&
                    isRowCell(elementFocusStatus) &&
                    isFocusable(elementFocusStatus) &&
                    isDomNodeVisible(
                        elementFocusStatus.elementRef
                            ? elementFocusStatus.elementRef
                            : {
                                  id: elementFocusStatus.elementId,
                                  elementId: elementFocusStatus.elementId,
                              },
                    )
                ) {
                    return elementFocusStatus;
                }
            }
        } else if (eventKey === "ArrowLeft") {
            for (let i = this.defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    i < index &&
                    elementFocusStatus.variableId === nextVariableId &&
                    elementFocusStatus.elementId !== currentElement.elementId &&
                    isRowCell(elementFocusStatus) &&
                    isFocusable(elementFocusStatus) &&
                    isDomNodeVisible(
                        elementFocusStatus.elementRef
                            ? elementFocusStatus.elementRef
                            : {
                                  id: elementFocusStatus.elementId,
                                  elementId: elementFocusStatus.elementId,
                              },
                    )
                ) {
                    return elementFocusStatus;
                }
            }
        }

        return null;
    }

    findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const nextChoiceInSameCell = this.findFocusableChoiceWithinCurrentVariable(
            currentElement,
            1,
        );
        if (nextChoiceInSameCell) {
            return nextChoiceInSameCell;
        }

        const variableIds = this.getVariableIdsForQuestion(currentElement.questionId);
        const currentVariableIndex = variableIds.indexOf(currentElement.variableId);
        if (currentVariableIndex >= 0 && currentVariableIndex < variableIds.length - 1) {
            const nextVariableId = variableIds[currentVariableIndex + 1];
            const firstChoiceInNextVariable = this.findFocusableChoiceWithinVariable(
                currentElement,
                nextVariableId,
                1,
            );
            if (firstChoiceInNextVariable) {
                return firstChoiceInNextVariable;
            }
        }

        return this.findNextFocusableElementWithinNextVariableCommon(currentElement);
    }

    findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const previousChoiceInSameCell = this.findFocusableChoiceWithinCurrentVariable(
            currentElement,
            -1,
        );
        if (previousChoiceInSameCell) {
            return previousChoiceInSameCell;
        }

        const variableIds = this.getVariableIdsForQuestion(currentElement.questionId);
        const currentVariableIndex = variableIds.indexOf(currentElement.variableId);
        if (currentVariableIndex > 0) {
            const previousVariableId = variableIds[currentVariableIndex - 1];
            const lastChoiceInPreviousVariable = this.findFocusableChoiceWithinVariable(
                currentElement,
                previousVariableId,
                -1,
            );
            if (lastChoiceInPreviousVariable) {
                return lastChoiceInPreviousVariable;
            }
        }

        return this.findLastFocusableElementWithinPreviousVariableCommon(currentElement);
    }
}
